// FIFO replay of queued offline writes, plus readCache patching so offline
// reloads reflect pending changes. Replay is single-flight and halts on the
// first network failure or 401 (resumes on the next trigger / re-auth).
import axios from 'axios';
import { queueAll, queueRemove, queueCount, deadLetterAdd, cacheGet, cachePut, cacheKeys } from './db';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export function notifyQueueChanged(syncing = false) {
  queueCount().then((pending) => {
    window.dispatchEvent(new CustomEvent('offline-queue-changed', { detail: { pending, syncing } }));
  }).catch(() => {});
}

// Patch cached GET /tasks responses so the list view stays coherent offline.
export async function patchCacheForWrite(method, url, body) {
  try {
    const keys = await cacheKeys();
    const taskListKeys = keys.filter((k) => k === '/tasks' || k.startsWith('/tasks?'));
    const completeMatch = url.match(/^\/tasks\/([^/]+)\/(complete|uncomplete)$/);
    const taskMatch = url.match(/^\/tasks\/([^/]+)$/);

    for (const key of taskListKeys) {
      const entry = await cacheGet(key);
      if (!entry) continue;
      // Adapters see pre-transform data, so cached payloads are JSON strings
      const wasString = typeof entry.data === 'string';
      let tasks = wasString ? JSON.parse(entry.data) : entry.data;
      if (!Array.isArray(tasks)) continue;
      if (method === 'post' && url === '/tasks' && body?.id) {
        tasks = [...tasks, { priority: 0, tags: [], list_ids: [], subtasks: [], completed_at: null, ...body }];
      } else if (method === 'put' && taskMatch) {
        tasks = tasks.map((t) => (t.id === taskMatch[1] ? { ...t, ...body } : t));
      } else if (method === 'delete' && taskMatch) {
        tasks = tasks.filter((t) => t.id !== taskMatch[1]);
      } else if (method === 'post' && completeMatch) {
        const done = completeMatch[2] === 'complete';
        tasks = tasks.map((t) => (t.id === completeMatch[1] ? { ...t, completed_at: done ? new Date().toISOString() : null } : t));
      } else {
        continue;
      }
      await cachePut(key, wasString ? JSON.stringify(tasks) : tasks);
    }
  } catch { /* cache patching is best-effort */ }
}

let replaying = false;

export async function replay() {
  if (replaying || !navigator.onLine) return;
  replaying = true;
  notifyQueueChanged(true);
  try {
    const ops = await queueAll();
    for (const op of ops) {
      const token = localStorage.getItem('device_token');
      try {
        await axios.request({
          baseURL: `${BACKEND_URL}/api`,
          url: op.url,
          method: op.method,
          data: op.body,
          headers: {
            'Content-Type': op.contentType || 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        });
        await queueRemove(op.seq);
      } catch (err) {
        const status = err.response?.status;
        if (!err.response) break; // still offline — retry on next trigger
        if (status === 401) break; // auth expired — resume after re-auth
        if (status === 404 && (op.method === 'put' || op.method === 'delete')) {
          await deadLetterAdd(op); await queueRemove(op.seq); continue;
        }
        if (status === 409 && op.method === 'post') {
          await queueRemove(op.seq); continue; // already landed (idempotent client id)
        }
        // Other HTTP errors: the server rejected the op — dead-letter it
        await deadLetterAdd({ ...op, error: status });
        await queueRemove(op.seq);
      }
    }
  } finally {
    replaying = false;
    notifyQueueChanged(false);
  }
}

let wired = false;
export function wireReplayTriggers() {
  if (wired) return;
  wired = true;
  let timer = null;
  const trigger = () => { clearTimeout(timer); timer = setTimeout(replay, 500); };
  window.addEventListener('online', trigger);
  document.addEventListener('visibilitychange', () => { if (!document.hidden) trigger(); });
  window.addEventListener('focus', trigger);
  trigger(); // drain anything left from a previous session
}
